import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

export default function ExportModule() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [exportType, setExportType] = useState("patrols");
  const [dateRange, setDateRange] = useState("30");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guardId, setGuardId] = useState("");
  const [routeId, setRouteId] = useState("");

  const [data, setData] = useState([]);
  const [guards, setGuards] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  /* ================= INIT ================= */
  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchFilters(token);
    fetchData(token);
  }, []);

  /* ================= FETCH ================= */
  const fetchDashboardData = async (token) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data?.companyDetails) {
        setDashboardData(res.data.data.companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchFilters = async (token) => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/reports/export/filters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuards(res.data.guards || []);
      setRoutes(res.data.routes || []);
    } catch (err) {
      console.error("Filters fetch error:", err);
      toast.error("Failed to load filters");
    }
  };

  const fetchData = async (token) => {
    try {
      setLoading(true);
      setError("");
      const token = getToken();

      const params = {
        exportType,
        dateRange,
        startDate,
        endDate,
        guardId,
        routeId,
      };

      const res = await axios.get(`${API_BASE_URL}/reports/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(res.data || []);
      if (res.data.length === 0) {
        setError("No data found for the selected filters");
        toast.info("No data found for the selected filters");
      } else {
        toast.success(`${res.data.length} records loaded successfully!`);
      }
    } catch (err) {
      console.error("Export data fetch error:", err);
      setError(err.response?.data?.message || "Error fetching data");
      toast.error(err.response?.data?.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const token = getToken();
    fetchData(token);
  };

  /* ================= EXPORT ================= */
  const exportCSV = () => {
    if (!data.length) {
      toast.error("No data to export");
      return;
    }

    try {
      const headers = Object.keys(data[0]);
      const rows = data.map((row) => headers.map((h) => row[h]));

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportType}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`CSV exported successfully! (${data.length} records)`);
    } catch (err) {
      console.error("CSV export error:", err);
      toast.error("Failed to export CSV");
    }
  };

  const exportExcel = () => {
    if (!data.length) {
      toast.error("No data to export");
      return;
    }

    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, `${exportType}_export_${new Date().toISOString().slice(0, 10)}.xlsx`);

      toast.success(`Excel exported successfully! (${data.length} records)`);
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error("Failed to export Excel");
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const exportTypeLabels = {
    patrols: "Patrols",
    incidents: "Incidents",
    checkpoints: "Checkpoints",
    guards: "Guards",
  };

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="exports"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Export Module
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Export patrol, incidents, checkpoints, and guard data
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Export Filters
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Select data type, date range, and filters for export
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Export Type
              </label>
              <select
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="patrols">Patrols</option>
                <option value="incidents">Incidents</option>
                <option value="checkpoints">Checkpoints</option>
                <option value="guards">Guards</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="7">7 Days</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {dateRange === "custom" && (
              <>
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                    style={{ fontFamily: FONT_UI }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                    style={{ fontFamily: FONT_UI }}
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Guard (Optional)
              </label>
              <select
                onChange={(e) => setGuardId(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All Guards</option>
                {guards.map((g) => (
                  <option key={g.id || g.member_id} value={g.id || g.member_id}>
                    {g.name || g.guard_name || g.fld_name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Route (Optional)
              </label>
              <select
                onChange={(e) => setRouteId(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All Routes</option>
                {routes.map((r) => (
                  <option key={r.route_id || r.id} value={r.route_id || r.id}>
                    {r.r_name || r.route_name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleRefresh}
                className="w-full border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {loading ? "Loading..." : "Refresh Data"}
              </button>
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Export Options
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                {data.length} records ready for export
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={exportCSV}
                disabled={loading || data.length === 0}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Export CSV
              </button>
              <button
                onClick={exportExcel}
                disabled={loading || data.length === 0}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Preview Data
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[var(--accent-faint)] text-[var(--accent)] text-[11px] font-extrabold">
              {data.length} records
            </span>
          </h3>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
              <span className="ml-3 text-[var(--text-muted)] text-[13px]">Loading data...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-[var(--red)] text-[13px] font-bold">
              {error}
            </div>
          ) : data.length ? (
            <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
              <table className="w-full text-[13px] font-['Inter',system-ui,sans-serif]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    {Object.keys(data[0]).map((key) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white"
                      >
                        {key.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        i % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      {Object.values(row).map((val, j) => (
                        <td
                          key={j}
                          className="px-4 py-3 text-[13px] text-[var(--text-primary)]"
                        >
                          {String(val) || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--text-muted)] text-[13px]">
              No data available. Please adjust your filters and refresh.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}